import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { GetCurrentUserDocument, ListOwnedPagesDocument, Role } from '../__generated__/graphql.js';
import type { AssetRouteParams } from '../pages/types.js';

interface AdminContextValue {
  isGeneralAdmin: boolean;
  isStaff: boolean;
  isAlveole: boolean;
  isAlveoleOrPartner: boolean;
  isPageAdmin: boolean;
  ownsPages: (pageIds: string[]) => boolean
  ownedPages: Array<{ id: string; slug?: string | null }>;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AdminContext = createContext<AdminContextValue | undefined>(undefined);

export const AdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading: isAuth0Loading } = useAuth0();
  const { assetSlug } = useParams<Partial<keyof AssetRouteParams>>();

  const { data: userData, loading: isLoadingUser } = useQuery(GetCurrentUserDocument, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated,
  });

  const { data: ownedPagesData, loading: isLoadingOwnedPages } = useQuery(ListOwnedPagesDocument, {
    fetchPolicy: 'cache-first',
    skip: !isAuthenticated,
  });

  const value = useMemo<AdminContextValue>(() => {
    const userRole = userData?.getCurrentUser.role ?? null;
    const ownedPages = ownedPagesData?.ownedPages ?? [];
    const isLoading = isAuth0Loading || isLoadingUser || isLoadingOwnedPages;

    const isGeneralAdmin = userRole === Role.Admin;
    const isStaff = userRole === Role.Admin || userRole === Role.Staff
    const isAlveole = userRole === Role.Admin || userRole === Role.Staff || userRole == Role.Beekeeper
    const isAlveoleOrPartner = userRole === Role.Admin || userRole === Role.Staff || userRole == Role.Beekeeper || userRole == Role.Partner

    const isPageAdmin = isAlveole || (assetSlug ? ownedPages.some((page) => page.slug === assetSlug) : false)

    const ownsPages = (pageIds: string[]): boolean => {
      return isAlveole || pageIds.every(pageId => ownedPages.some((ownedPage) => ownedPage.id === pageId));
    }

    return {
      isGeneralAdmin,
      isStaff,
      isAlveole,
      isAlveoleOrPartner,
      isPageAdmin,
      ownsPages,
      ownedPages,
      isLoading,
      isAuthenticated,
    };
  }, [userData, ownedPagesData, assetSlug, isAuth0Loading, isLoadingUser, isLoadingOwnedPages, isAuthenticated]);

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export const useAdmin = (): AdminContextValue => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
